import { useEffect, useState } from 'react';
import InnerHTML from 'dangerously-set-html-content';
import styles from '../article.module.css';

export const StyledVideo = (props: StyledVideoProps) => {
	const {
		id,
		expandfullwidth,
		vidtype,
		bleed,
		caption,
		captionenabled,
		credit,
		creditenabled,
		vidid,
		moviepath,
		autoplay,
		hostedurl,
		loop,
		aspectRatio,
		controlsenabled,
		linktype,
		weblink,
		params,
	} = props;
	return (
		<Video
			id={id}
			videoId={vidid}
			type={vidtype}
			bleed={bleed === 'on'}
			hostedUrl={hostedurl}
			expandFullWidth={expandfullwidth === 'on'}
			caption={caption}
			captionEnabled={captionenabled === 'on'}
			credit={credit}
			aspectRatio={aspectRatio}
			creditEnabled={creditenabled === 'on'}
			moviePath={moviepath}
			params={params}
			autoplay={autoplay === 'on'}
			loop={loop === 'on'}
			controlsEnabled={controlsenabled === 'on'}
			linkType={linktype}
			webLink={weblink}
		/>
	);
};

interface StyledVideoProps {
	id: string;
	vidid: string;
	expandfullwidth: string;
	vidtype: 'youtube' | 'vimeo' | 'movie' | 'hosted' | 'tiktok' | 'brightcove';
	bleed: string;
	caption: string;
	aspectRatio: '16by9' | '4by3' | 'auto';
	captionenabled: string;
	credit: any;
	creditenabled: string;
	moviepath: string;
	hostedurl: string;
	autoplay: string;
	loop: string;
	controlsenabled: string;
	linktype: string;
	weblink: string;
	params: any;
}

export const Video = (props: VideoProps) => {
	const {
		id,
		videoId,
		type,
		marginTop,
		marginBottom,
		bleed,
		fullWidth,
		expandFullWidth,
		hostedUrl,
		caption,
		captionEnabled,
		credit,
		creditEnabled,
		moviePath,
		autoplay,
		loop,
		aspectRatio,
		controlsEnabled,
		linkType,
		webLink,
		params,
	} = props;
	let component = null;
	const containerStyle: any = {
		marginTop,
		marginBottom,
	};
	const componentStyle: any = {};
	if (fullWidth) {
		containerStyle.minWidth = '100%';
		containerStyle.width = '100%';
	}

	if (expandFullWidth) {
		componentStyle.minWidth = '100%';
		componentStyle.width = '100%';
	}

	switch (type) {
		case 'youtube':
			component = (
				<iframe
					width="100%"
					title={'Video'}
					height="480"
					style={componentStyle}
					src={`https://www.youtube.com/embed/${videoId}?rel=0&enablejsapi=1`}
					allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
					allowFullScreen
				/>
			);
			break;
		case 'vimeo':
			component = (
				<iframe
					title={'Video'}
					src={`https://player.vimeo.com/video/${videoId}`}
					width="100%"
					height="480"
					style={componentStyle}
					allow="autoplay; fullscreen"
					allowFullScreen
				/>
			);
			break;
		case 'brightcove':
			if (params?.accountID && params?.videoID) {
				component = (
					<iframe
						src={`https://players.brightcove.net/${params.accountID}/default_default/index.html?videoId=${params.videoID}`}
						allowFullScreen
						className={styles['brightcove']}
						allow="encrypted-media"
					/>
				);
			}
			break;
		case 'tiktok':
			component = (
				<TikTok username={params?.username} videoID={params?.videoID} />
			);
			break;
		case 'hosted':
		case 'movie':
			component = (
				<Movie
					autoplay={autoplay}
					loop={loop}
					controls={controlsEnabled}
					style={componentStyle}
					url={type === 'hosted' ? hostedUrl : moviePath}
				/>
			);
			break;
		default:
			component = null;
	}

	const className = [styles['video'], 'media', 'video'];
	if (bleed && !fullWidth) {
		className.push('bleed');
	}

	if (aspectRatio === 'auto') {
		className.push(styles['auto']);
	}

	if ((linkType === 'web' || linkType === 'page') && webLink) {
		return (
			<div id={id} className={className.join(' ')} style={containerStyle}>
				<a href={webLink}>{component}</a>
				{creditEnabled && credit ? <Credit content={credit} /> : null}
				{captionEnabled && caption ? (
					<Caption content={caption} />
				) : null}
			</div>
		);
	}

	return (
		<div id={id} className={className.join(' ')} style={containerStyle}>
			{component}
			{creditEnabled && credit ? <Credit content={credit} /> : null}
			{captionEnabled && caption ? <Caption content={caption} /> : null}
		</div>
	);
};

interface VideoProps {
	id?: string;
	videoId?: string;
	type?: string;
	bleed?: boolean;
	fullWidth?: boolean;
	expandFullWidth?: boolean;
	caption?: string;
	captionEnabled?: boolean;
	credit?: string;
	creditEnabled: boolean;
	marginTop: number;
	marginBottom: number;
	moviePath: string;
	hostedUrl: string;
	autoplay: boolean;
	loop: boolean;
	controlsEnabled: boolean;
	aspectRatio: '16by9' | '4by3' | 'auto';
	linkType: string;
	webLink: string;
	params: any;
}

Video.defaultProps = {
	marginTop: 0,
	marginBottom: 0,
};

const Caption = ({ content = '' }: CaptionProps) => {
	return <figcaption dangerouslySetInnerHTML={{ __html: content }} />;
};

interface CaptionProps {
	content: string;
}

const Credit = ({ content = '' }: CreditProps) => {
	return (
		<cite
			className={styles['credit']}
			dangerouslySetInnerHTML={{ __html: content }}
		/>
	);
};

interface CreditProps {
	content: string;
}

const Movie = (props: MovieProps) => {
	const { url, autoplay, loop, controls, style } = props;
	const [opts, setOpts] = useState({});
	useEffect(() => {
		const attributes: any = {};

		if (autoplay) {
			attributes.autoPlay = true;
			attributes.muted = true;
			attributes.playsInline = true;
			attributes.preload = 'meta';
		}
		if (controls) {
			attributes.controls = true;
		}
		if (loop) {
			attributes.loop = true;
		}
		setOpts(attributes);
	}, [url, autoplay, loop, controls]);

	if (!url) {
		return null;
	}

	const extension = url.split('.').pop();
	return (
		// eslint-disable-next-line jsx-a11y/media-has-caption
		<video {...opts} style={style}>
			<source src={url} type={`video/${extension}`} />
			Sorry, your browser does not support embedded videos.
		</video>
	);
};

interface MovieProps {
	url: string;
	autoplay: boolean;
	loop: boolean;
	controls: boolean;
	style: any;
}

function TikTok(props: TikTokProps) {
	const { username, videoID } = props;
	const [content, setContent] = useState('');
	const [error, setError] = useState('');

	useEffect(() => {
		if (content || !username || !videoID) return;
		const url = `https://www.tiktok.com/@${username}/video/${videoID}`;
		fetch(`https://www.tiktok.com/oembed?url=${url}`)
			.then((response) => response.json())
			.then(({ html }) => {
				setContent(html);
			})
			.catch((err) => setError(err.message));
	}, [content]);

	if (error) {
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					color: '#fff',
					background: 'rgba(255,69,58,1)',
					padding: 20,
					fontWeight: 'bold',
				}}
			>
				{error}
			</div>
		);
	}

	if (content) {
		return <InnerHTML html={content} />;
	}

	return null;
}

interface TikTokProps {
	username: string;
	videoID: string;
}
